import capitalize from "lodash/capitalize";
import noop from "lodash/noop";
import React from "react";
import styled from "styled-components";
import { colors } from "styles/color-tokens";
import {
  PlanPeriodUnit,
  SkedPlanCodeName,
  UpsellFeatureType,
  UpsellPlansType,
  UpsellPlanType,
} from "./types";
import CheckIcon from "./icons/check.svg";
import UpgradeArrow from "./icons/upgrade-arrow.svg";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { openSupportChat } from "libs/support";
import { Spinner } from "@/components/ui/spinner";
import {
  GenericErrorMessage,
  renderPriceWithSymbol,
} from "./common-components";
import { findPlanByPlanCodeName } from "./plan-utils";

export interface UpsellPlansContext {
  plans: UpsellPlansType;
  periodUnit: PlanPeriodUnit;
  currency: string;
  isLegacyPlan: boolean;
  onUpgradeClick: () => void;
  currentPlan: SkedPlanCodeName;
  isLoading?: boolean;
  isError?: boolean;
  includeGst: boolean;
  triggerFeature: UpsellFeatureType;
}

// SKE-3629 (2026-05-08): post-2026-04-28 rebrand mapping. Returns the
// public tier name of the plan one slug below. Slug→tier shifted:
//   sked-essentials   Launch     → Grow
//   sked-professional Grow       → Accelerate
//   sked-enterprise   Accelerate → Enterprise
// So the "previous" tier names also shifted up one slot.
function getPrevPlan(plan: SkedPlanCodeName) {
  switch (plan) {
    case "sked-essentials": {
      return "basic";
    }
    case "sked-professional": {
      return "grow";
    }
    case "sked-enterprise": {
      return "accelerate";
    }
  }
  return "basic";
}

export const UpsellUpgradePlan = ({
  toPlanName,
  plansContext,
  fromPlanFeatures,
  toPlanFeatures,
  highlightItemIndex,
  fromPlan,
  toPlanLabel,
  fromPlanLabel,
}: {
  toPlanName: SkedPlanCodeName;
  plansContext: UpsellPlansContext;
  fromPlanFeatures: string[];
  toPlanFeatures: string[];
  highlightItemIndex: number | undefined;
  fromPlan: UpsellPlanType | undefined;
  toPlanLabel: string | undefined;
  fromPlanLabel: string | undefined;
}) => {
  const { plans, currency, periodUnit, isLegacyPlan, isLoading, isError } =
    plansContext;

  if (isLegacyPlan) {
    return <UpsellLegacySection feature={plansContext.triggerFeature} />;
  }

  if (isLoading) {
    return (
      <LegacyMainContainer>
        <div className="absolute inset-0 flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      </LegacyMainContainer>
    );
  }
  if (isError) {
    return (
      <LegacyMainContainer>
        <GenericErrorMessage />
      </LegacyMainContainer>
    );
  }

  const toPlan = findPlanByPlanCodeName(plans, periodUnit, toPlanName);

  if (!toPlan) {
    return null;
  }

  return (
    <PlanWrap>
      {fromPlan && (
        <>
          <UpsellPlan
            currency={currency}
            periodUnit={periodUnit}
            plan={fromPlan}
            planTitle={fromPlanLabel || fromPlan.planLabel}
            features={fromPlanFeatures}
            featureTitle="Key features:"
            buttonType="current-plan-disabled"
            boxStyle={{
              width: 300,
            }}
            includeGst={plansContext.includeGst}
          />
          <UpgradeArrowContainer>
            <UpgradeArrow />
          </UpgradeArrowContainer>
        </>
      )}

      <UpsellPlan
        currency={currency}
        periodUnit={periodUnit}
        plan={toPlan}
        planTitle={toPlanLabel}
        features={toPlanFeatures}
        featureTitle={`Everything in ${capitalize(
          getPrevPlan(toPlanName)
        )}, plus:`}
        onUpgradeClick={plansContext.onUpgradeClick}
        buttonType="upgrade"
        highlightItemIndex={highlightItemIndex}
        boxStyle={{
          border: `1px solid ${colors.primary500}`,
          width: 320,
        }}
        includeGst={plansContext.includeGst}
      />
    </PlanWrap>
  );
};

const UpgradeArrowContainer = styled.div`
  margin-left: 16px;
  margin-right: 16px;
`;

export const UpsellPlan = ({
  plan,
  periodUnit,
  currency,
  features,
  featureTitle,
  onUpgradeClick,
  buttonType,
  highlightItemIndex,
  boxStyle,
  buttonWidth = "100%",
  includeGst,
  planTitle,
}: {
  plan: UpsellPlanType;
  periodUnit: PlanPeriodUnit;
  currency: string;
  features: string[];
  featureTitle: string;
  highlightItemIndex?: number;
  onUpgradeClick?: () => void;
  boxStyle?: React.CSSProperties;
  buttonType:
    "none" | "current-plan-disabled" | "too-many" | "downgrade" | "upgrade";
  buttonWidth?: string;
  includeGst: boolean;
  planTitle?: string;
}) => {
  return (
    <PlanComponent>
      <Plan onClick={noop} style={boxStyle}>
        <div className="mobileNamePrice">
          <PlanName>{planTitle || plan.planName}</PlanName>
          <PlanPrice>
            <h4>
              {renderPriceWithSymbol({
                currency,
                periodUnit,
                price: formatPrice(plan.price),
                showBilledPerYear: periodUnit === "year",
              })}
            </h4>
            <PlanPriceDetail>
              <span>/month</span>
              <span style={{ whiteSpace: "nowrap" }}>
                for {plan.includedAccounts} social accounts
              </span>
            </PlanPriceDetail>
          </PlanPrice>
          {periodUnit === "year" && (
            <BilledPerYear>
              Billed as{" "}
              {renderPriceWithSymbol({
                currency,
                periodUnit,
                price: formatPrice(plan.price),
                showBilledPerYear: false,
              })}{" "}
              per year
            </BilledPerYear>
          )}
        </div>
        {plan.includesGst && includeGst && (
          <GstIncludeText>(including 10% GST)</GstIncludeText>
        )}
        <FeaturesSection
          items={features}
          title={featureTitle}
          highlightItemIndex={highlightItemIndex}
        />

        {(() => {
          switch (buttonType) {
            case "none": {
              return (
                <span
                  data-testid={`button-${plan.planCodeName}-${buttonType}`}
                />
              );
            }
            case "too-many": {
              return (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <UpgradeButtonContainer
                      data-testid={`button-${plan.planCodeName}-${buttonType}`}
                      buttonWidth={buttonWidth}
                      textColor="#969696"
                      bgColor={undefined}
                    >
                      <Button onClick={noop} disabled>
                        Too many accounts for this plan
                      </Button>
                    </UpgradeButtonContainer>
                  </TooltipTrigger>
                  <TooltipContent>
                    You have too many accounts added for this plan. Check with
                    support to discuss your options.
                  </TooltipContent>
                </Tooltip>
              );
            }
            case "current-plan-disabled": {
              return (
                <UpgradeButtonContainer
                  data-testid={`button-${plan.planCodeName}-${buttonType}`}
                  buttonWidth={buttonWidth}
                  textColor="white"
                  bgColor="#969696"
                >
                  <Button onClick={noop} disabled>
                    Current plan
                  </Button>
                </UpgradeButtonContainer>
              );
            }
            case "downgrade": {
              return (
                <UpgradeButtonContainer
                  data-testid={`button-${plan.planCodeName}-${buttonType}`}
                  buttonWidth={buttonWidth}
                  svgSize={16}
                  bgColor="#fafafa"
                  textColor={colors.primary500}
                >
                  <Button
                    onClick={onUpgradeClick || noop}
                    size="md"
                    variant="blue-secondary"
                  >
                    Downgrade plan
                  </Button>
                </UpgradeButtonContainer>
              );
            }
            case "upgrade": {
              return (
                <UpgradeButtonContainer
                  data-testid={`button-${plan.planCodeName}-${buttonType}`}
                  buttonWidth={buttonWidth}
                  svgSize={16}
                  bgColor={colors.lime500}
                  textColor={colors.neutral700}
                >
                  <Button
                    onClick={onUpgradeClick || noop}
                    size="md"
                    className="bg-lime-500 text-neutral-700 hover:bg-lime-600"
                  >
                    Upgrade plan
                    <i className="fa-regular fa-rocket" />
                  </Button>
                </UpgradeButtonContainer>
              );
            }
          }
        })()}
      </Plan>
    </PlanComponent>
  );
};

const GstIncludeText = styled.div`
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  text-align: center;
  margin-bottom: 16px;
`;

const UpgradeButtonContainer = styled.div<{
  buttonWidth: string;
  bgColor?: string;
  textColor?: string;
  svgSize?: number;
}>`
  display: flex;
  align-items: stretch;
  margin-top: 10px;
  justify-content: center;
  height: 40px;

  > button {
    > div > svg {
      width: ${(p) => (p.svgSize ? `${p.svgSize}px` : "30px")};
      height: ${(p) => (p.svgSize ? `${p.svgSize}px` : "30px")};
    }
  }

  > button {
    width: ${(props) => (props.buttonWidth ? props.buttonWidth : "auto")};
    > p {
      ${(props) => (props.textColor ? `color: ${props.textColor};` : "")}
    }
  }
  ${(p) =>
    p.bgColor &&
    `
    > button {
      background-color: ${p.bgColor} !important;
      &:hover {
        background-color: ${p.bgColor} !important;
      }
    }
  `}
`;

const FeaturesSection = ({
  items,
  title,
  highlightItemIndex,
}: {
  items: string[];
  title: string;
  highlightItemIndex: number | undefined;
}) => {
  return (
    <FeaturesWrap>
      <FeaturesTitle>{title}</FeaturesTitle>
      <Features>
        {items.map((feature, idx) => (
          <FeatureItem key={feature} highlightItem={highlightItemIndex === idx}>
            {feature && <CheckIcon />}
            {feature || "\u00A0"}
          </FeatureItem>
        ))}
      </Features>
    </FeaturesWrap>
  );
};

const FeatureItem = styled.li<{ highlightItem: boolean }>`
  color: var(--Text-T01, ${colors.neutral800});
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  /* Web App/Paragraph 2 Regular */
  font-family: Inter, Helvetica, Arial, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */

  ${(props) => {
    return (
      props.highlightItem &&
      `
      color: ${colors.primary500};
      font-weight: 600;
      `
    );
  }}
`;

const PlanWrap = styled.div`
  display: flex;
  column-gap: 7px;
  margin: 16px 0;
  align-items: center;
`;
const PlanComponent = styled.div`
  flex-shrink: 0;
`;

export const Plan = styled.div`
  position: relative;
  border-radius: 8px;
  border: 1px solid var(--Neutral-N100, #e6e6e6);
  flex: 1;
  padding: 24px 16px;
  height: 100%;
  box-sizing: border-box;
  transition: height 0.2s ease-out;
  display: flex;
  flex-direction: column;
`;

const PlanName = styled.div`
  color: var(--Text-T01, ${colors.neutral800});
  text-align: center;

  /* Web App/Heading 4 */
  font-family: "Gotham SSm A", "Gotham SSm B", Helvetica, Arial, sans-serif;

  text-transform: capitalize;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 150% */
`;

const PlanPrice = styled.div`
  text-align: center;
  font-size: 16px;
  font-weight: 900;
  margin: 8px auto;
  color: ${colors.neutral700};
  display: flex;
  align-items: center;
  justify-content: center;

  h4 {
    color: var(--Text-T01, ${colors.neutral800});
    text-align: center;
    margin: 0;

    /* Web App/Heading 3 */
    font-family: "Gotham SSm A", "Gotham SSm B", Helvetica, Arial, sans-serif;
    font-size: 24px;
    font-style: normal;
    font-weight: 500;
    line-height: 32px; /* 133.333% */
  }
`;

const PlanPriceDetail = styled.div`
  margin-left: 4px;
  color: var(--Text-T01, ${colors.neutral800});
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  /* Text xs/Semibold */
  font-family: Inter, Helvetica, Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 18px; /* 150% */
`;

const BilledPerYear = styled.div`
  color: var(--Text-T01, ${colors.neutral800});
  text-align: center;
  margin-bottom: 8px;

  /* Web App/Paragraph 3 Regular */
  font-family: Inter, Helvetica, Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px; /* 133.333% */
`;

const FeaturesWrap = styled.div`
  flex-grow: 1;
`;
const FeaturesTitle = styled.div`
  color: var(--Text-T01, ${colors.neutral800});

  /* Web App/Paragraph 2 Semi Bold */
  font-family: Inter, Helvetica, Arial, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px; /* 142.857% */
  margin-bottom: 8px;
`;
const Features = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    svg {
      width: 12px;
      margin-right: 8px;
    }
  }
`;

export const UpsellLegacySection = ({
  feature,
}: {
  feature: UpsellFeatureType;
}) => {
  const supportText =
    feature === "unknown"
      ? "I'm interested in upgrading my plan. Can you please help me?"
      : `I'd like to add more accounts. Can you please help me?`;
  return (
    <LegacyMainContainer>
      <UpgradePlanTextRequestText>
        You are currently on a legacy plan. Please contact support to change or
        upgrade your plan.
      </UpgradePlanTextRequestText>
      <UpgradeButtonContainer
        buttonWidth="100%"
        style={{ width: 288 }}
        svgSize={16}
      >
        <Button
          className="bg-yellow-400 text-neutral-800 hover:bg-yellow-500"
          onClick={() => {
            openSupportChat(supportText, "upsell");
          }}
          size="md"
        >
          Contact Support
          <i className="fa-regular fa-headphones" />
        </Button>
      </UpgradeButtonContainer>
    </LegacyMainContainer>
  );
};

const LegacyMainContainer = styled.div`
  width: 700px;
  height: 370px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
`;

export const UpgradePlanTextRequestText = styled.div`
  font-family: Inter, Helvetica, Arial, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: ${colors.neutral800};
  padding-top: 16px;
  padding-bottom: 16px;
`;

const formatPrice = (price: number) => {
  price = price / 100;
  return Number.isInteger(price)
    ? String(price)
    : Number.isNaN(price)
      ? price
      : price.toFixed(2);
};
